import { NgModule } from '@angular/core';
import { RouterModule } from '@angular/router';

// modules
import { GroupsSharedModule } from '@app/groups/groups-shared.module';
import { InputsModule } from '@app/inputs/inputs.module';
import { MarkdownModule } from '@app/markdown/markdown.module';
import { OpportunitiesSharedModule } from '@app/opportunities/opportunities-shared.module';
import { OrgsSharedModule } from '@app/orgs/orgs-shared.module';
import { SharedModule } from '@app/shared/shared.module';
import { TagsModule } from '@app/tags/tags.module';
import { SearchFiltersModule } from './components/search-filters/search-filters.module';
import { SearchSharedModule } from './search-shared.module';
import { OpportunitiesModalsModule } from '@app/opportunities/opportunities-modals.module';
import { NavigationModule } from '@app/navigation/navigation.module';
import { ResourceLauncherModule } from '@app/resource-launcher/resource-launcher.module';
import { MentoringModule } from '@app/mentoring/mentoring.module';

import {
  SearchViewComponent,
  SearchReactiveStoreModule,
} from './components/search-view';

// components
import { SearchGroupsViewComponent } from './components/search-groups-view/search-groups-view.component';
import { SearchGroupsComponent } from './components/search-groups/search-groups.component';
import { SearchNoResultsComponent } from './components/search-no-results/search-no-results.component';
import { SearchOpportunitiesComponent } from './components/search-opportunities/search-opportunities.component';
import { SearchPeopleComponent } from './components/search-people/search-people.component';
import { SearchProviderViewComponent } from './components/search-provider-view/search-provider-view.component';
import { SearchLearningsComponent } from './components/search-learnings/search-learnings.component';
import { SearchResultsComponent } from './components/search-results/search-results.component';
import { SearchResultsPaginationComponent } from './components/search-results-pagination/search-results-pagination.component';
import { SearchSidebarErrorComponent } from './components/search-sidebar-error/search-sidebar-error.component';
import { SearchSidebarNoResultsComponent } from './components/search-sidebar-no-results/search-sidebar-no-results.component';
import { SearchSkillsViewComponent } from './components/search-skills-view/search-skills-view.component';
import { SearchSkillsComponent } from './components/search-skills/search-skills.component';
import { SearchProviderNoResultsComponent } from './components/search-provider-no-results/search-provider-no-results.component';
import { SearchOpportunitiesViewComponent } from './components/search-opportunities-view/search-opportunities-view.component';
import { SearchPeopleViewComponent } from './components/search-people-view/search-people-view.component';
import { SearchLandingPageComponent } from './shared/search-landing-page/search-landing-page.component';
import { SearchMentorsViewComponent } from './components/search-mentors-view/search-mentors-view.component';
import { SearchTypoSuggestionComponent } from './components/search-typo-suggestion/search-typo-suggestion.component';
import { SearchRelatedSkillsComponent } from './components/search-related-skills/search-related-skills.component';
import { SearchEndorsedResultsComponent } from './components/search-endorsed-results/search-endorsed-results.component';
import { SearchResultsHeaderComponent } from './components/search-results-header/search-results-header.component';
import { SearchTabsComponent } from './components/search-tabs/search-tabs.component';
import { SearchFiltersSidebarComponent } from './components/search-filters-sidebar/search-filters-sidebar.component';
import { SearchFilterSidebarComponent } from './components/search-filters-sidebar/search-filter-sidebar/search-filter-sidebar.component';
import { SearchFilterSidebarOptionsComponent } from './components/search-filters-sidebar/search-filter-sidebar/search-filter-sidebar-options/search-filter-sidebar-options.component';
import { SearchMobileFiltersComponent } from './components/search-filters-sidebar/search-mobile-filters/search-mobile-filters.component';
import { SearchMobileFiltersModalComponent } from './components/search-filters-sidebar/search-mobile-filters/search-mobile-filters-modal/search-mobile-filters-modal.component';
import { SearchFilterSidebarRangeOptionsComponent } from './components/search-filters-sidebar/search-filter-range-sidebar/search-filter-sidebar-range-options/search-filter-sidebar-range-options.component';
import { SearchRangeFilterSidebarComponent } from './components/search-filters-sidebar/search-filter-range-sidebar/search-filter-range-sidebar.component';

// services
import { AuthGuardService } from '@app/routing/services/auth-guard.service';
import { OpportunityDetailsResolver } from '@app/opportunities/routes/resolves/opportunity-details.resolver';
import { OPPORTUNITY_ROUTES } from '@app/opportunities/routes/opportunities.routes';
import { OpportunityViewComponent } from '@app/opportunities/components/opportunity-view/opportunity-view.component';
import { OpportunitiesInsightsGuardService } from '@app/career/services/opportunities-insights-guard.service';
import { OpportunitiesViewGuardService } from '@app/routing/services/opportunities-view-guard.service';
import { OpportunityViewGuardService } from '@app/opportunities/routes/guards/opportunity-view-guard.service';
import { OpportunityManageGuardService } from '@app/opportunities/routes/guards/opportunity-manage-guard.service';
import { OpportunityInterestedGuardService } from '@app/opportunities/routes/guards/opportunity-interested-guard.service';
import { OpportunitiesNewBrowseGuardService } from '@app/career/services/opportunities-new-browse-guard.service';
import { SwiperModule } from 'swiper/angular';
import { SearchFeaturedCarouselComponent } from './components/search-featured-carousel/search-featured-carousel.component';
import { LearningViewComponent } from './components/learning-view/learning-view.component';

/**
 * NOTE: This module is lazyloaded, do not import it directly into another component
 *
 * If you need to use components/services that are specific to this module, move
 * them out into the `search-shared.module` and import that that module into your
 * module instead.
 */
@NgModule({
  declarations: [
    LearningViewComponent,
    SearchViewComponent,
    SearchLearningsComponent,
    SearchResultsComponent,
    SearchResultsPaginationComponent,
    SearchPeopleComponent,
    SearchPeopleViewComponent,
    SearchSkillsComponent,
    SearchSkillsViewComponent,
    SearchOpportunitiesComponent,
    SearchOpportunitiesViewComponent,
    SearchGroupsComponent,
    SearchGroupsViewComponent,
    SearchProviderViewComponent,
    SearchNoResultsComponent,
    SearchSidebarNoResultsComponent,
    SearchSidebarErrorComponent,
    SearchLandingPageComponent,
    SearchRelatedSkillsComponent,
    SearchProviderNoResultsComponent,
    SearchMentorsViewComponent,
    SearchTypoSuggestionComponent,
    SearchEndorsedResultsComponent,
    SearchResultsHeaderComponent,
    SearchTabsComponent,
    SearchFiltersSidebarComponent,
    SearchFilterSidebarComponent,
    SearchFilterSidebarOptionsComponent,
    SearchMobileFiltersComponent,
    SearchMobileFiltersModalComponent,
    SearchFeaturedCarouselComponent,
    SearchRangeFilterSidebarComponent,
    SearchFilterSidebarRangeOptionsComponent,
  ],
  imports: [
    RouterModule.forChild([
      {
        path: '',
        component: LearningViewComponent,
        children: [
          {
            path: '',
            redirectTo: 'search',
          },
          {
            path: 'search',
            component: SearchViewComponent,
            canActivate: [AuthGuardService],
          },
          {
            path: 'opportunities',
            component: SearchOpportunitiesViewComponent,
            canActivate: [OpportunitiesViewGuardService],
            children: [
              {
                path: ':id',
                component: OpportunityViewComponent,
                canActivate: [
                  OpportunitiesNewBrowseGuardService,
                  OpportunityViewGuardService,
                ],
                data: {
                  isBrowsePreviewView: true,
                },
                resolve: {
                  details: OpportunityDetailsResolver,
                },
                children: OPPORTUNITY_ROUTES,
              },
            ],
          },
          {
            path: 'skills',
            component: SearchSkillsViewComponent,
          },
          {
            path: 'people',
            component: SearchPeopleViewComponent,
          },
          {
            path: 'groups',
            component: SearchGroupsViewComponent,
          },
          {
            path: 'provider',
            component: SearchProviderViewComponent,
          },
          {
            path: 'mentors',
            component: SearchMentorsViewComponent,
          },
        ],
      },
    ]),
    SharedModule,
    TagsModule,
    InputsModule,
    MarkdownModule,
    OpportunitiesModalsModule,
    OpportunitiesSharedModule,
    SearchFiltersModule,
    GroupsSharedModule,
    OrgsSharedModule,
    SearchSharedModule,
    SearchReactiveStoreModule,
    NavigationModule,
    ResourceLauncherModule,
    MentoringModule,
    SwiperModule,
  ],
  providers: [
    OpportunitiesInsightsGuardService,
    OpportunitiesViewGuardService,
    OpportunityViewGuardService,
    OpportunityManageGuardService,
    OpportunityInterestedGuardService,
    OpportunitiesNewBrowseGuardService,
  ],
})
export class SearchModule {}
